import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ClipboardList, Search } from 'lucide-react';
import type { OrderFilter } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, Input, ListSkeleton, cn } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useOrders } from '../lib/orders/use-orders.js';
import { formatCurrency, formatDate } from '../lib/format.js';

/**
 * The order book.
 *
 * The filters are the ones a shop asks for out loud — "what is due today", "what is late",
 * "what can I hand over", "who still owes me" — rather than a status dropdown. Each tab carries
 * its count, so the owner sees "3 late" without opening the tab, which is the whole reason to
 * look at this screen in the morning.
 */

/** The tabs that earn a place on a 360px screen, in the order a shop asks them. */
const VISIBLE_FILTERS: readonly OrderFilter[] = [
  'due_today',
  'overdue',
  'ready',
  'unpaid',
  'in_progress',
  'all',
];

export function OrdersPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [filter, setFilter] = useState<OrderFilter>('due_today');
  const [query, setQuery] = useState('');

  const orders = useOrders({ filter, ...(query.trim() ? { q: query.trim() } : {}) });
  const rows = orders.data?.items ?? [];
  const counts = orders.data?.counts ?? {};

  return (
    <>
      <PageHeader
        title={t('orders.title')}
        description={t('orders.description')}
        action={
          <div className="flex flex-wrap gap-2">
            {/* The money screens live one tap from the order book rather than in the bottom
                navigation, which is full and whose fifth slot belongs to the board. */}
            {role !== 'tailor' ? (
              <Button asChild variant="outline">
                <Link to="/payments">{t('payments.title')}</Link>
              </Button>
            ) : null}
            <Button asChild>
              <Link to="/orders/new">{t('orders.book')}</Link>
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label={t('orders.searchLabel')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('orders.searchPlaceholder')}
            className="pl-9"
          />
        </div>

        {/* Scrolls sideways on a phone rather than wrapping into three rows of chips. */}
        <div
          role="tablist"
          aria-label={t('orders.title')}
          className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1"
        >
          {VISIBLE_FILTERS.map((one) => {
            const active = filter === one;
            const count = counts[one];
            return (
              <button
                key={one}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(one)}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'border-transparent bg-primary text-primary-foreground'
                    : 'border-border hover:bg-accent',
                )}
              >
                {t(`orders.filter.${one}`)}
                {count !== undefined && count > 0 ? (
                  <span
                    className={cn(
                      'rounded-full px-1.5 text-xs tabular-nums',
                      active ? 'bg-primary-foreground/20' : 'bg-muted',
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {orders.isPending ? (
          <ListSkeleton rows={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={query.trim() || filter !== 'all' ? t('orders.noMatch') : t('orders.emptyTitle')}
            description={
              query.trim() || filter !== 'all'
                ? t('orders.noMatchHint')
                : t('orders.emptyDescription')
            }
            action={
              <Button asChild>
                <Link to="/orders/new">{t('orders.book')}</Link>
              </Button>
            }
          />
        ) : (
          <>
            <p className="text-sm text-muted-foreground" role="status">
              {t('orders.resultCount', { count: orders.data?.total ?? rows.length })}
            </p>
            <ul className="flex flex-col gap-2">
              {rows.map((order) => (
                <li key={order.id}>
                  <Link
                    to={`/orders/${order.id}`}
                    aria-label={t('orders.openOrder', { number: order.orderNumber })}
                    className="flex flex-col gap-2 rounded-lg border border-border p-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-row sm:items-center sm:gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 font-medium">
                        {order.customerName}
                        {order.priority ? (
                          <Badge variant="destructive">{t('orders.urgent')}</Badge>
                        ) : null}
                        {order.cancelledAt ? (
                          <Badge variant="secondary">{t('orders.cancelledBadge')}</Badge>
                        ) : null}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {order.orderNumber} · {t('orders.itemCount', { count: order.itemCount })} ·{' '}
                        {t(`stage.${order.stage}`)}
                      </p>
                    </div>

                    <dl className="flex shrink-0 gap-4 text-sm sm:gap-6 sm:text-right">
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          {t('orders.promisedDate')}
                        </dt>
                        <dd className="font-medium">
                          {order.promisedDate
                            ? formatDate(order.promisedDate)
                            : t('orders.noPromisedDate')}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">{t('orders.total')}</dt>
                        <dd className="font-medium tabular-nums">
                          {formatCurrency(order.totalPaise, { compact: true })}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">{t('orders.balance')}</dt>
                        <dd className="font-medium tabular-nums">
                          {order.balancePaise > 0
                            ? formatCurrency(order.balancePaise, { compact: true })
                            : t('orders.settled')}
                        </dd>
                      </div>
                    </dl>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </>
  );
}

export default OrdersPage;
